import I from 'immutable';

import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {SORT_DESC} from 'app/utils/frontendConstants';
import {NONE} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';

export function displayLayerInMenu(
  featureData: I.List<I.ImmutableOf<ApiFeatureData>> | null,
  feature: I.ImmutableOf<ApiFeature>,
  scenes: I.ImmutableListOf<[string, string]> | undefined,
  layerKey: string,
  featureCollection: I.ImmutableOf<ApiFeatureCollection>,
  options?: {includeLayersWithoutImagery?: boolean}
) {
  // we always want to show the Basemap
  if (layerKey === NONE) {
    return true;
  }

  const layer = layerUtils.getLayer(layerKey);

  // check whether the feature associated with this layer is outside the geographic bounds
  // where this layer is available
  const featureOutsideAvailableRegion =
    layer.polygonExtent && !featureCollectionUtils.featureInBounds(feature, layer.polygonExtent);

  // If the layer has a `filter` property value, call it to determine if
  // we should exclude or include the layer; otherwise, include it.
  const {filter: layerFilter} = layer;
  const layerGetsFiltered = layerFilter ? layerFilter(featureCollection) : true;

  return (
    layerGetsFiltered &&
    !featureOutsideAvailableRegion &&
    (!!options?.includeLayersWithoutImagery ||
      layerHasProcessedImagery(featureData, scenes, layerKey))
  );
}

export function layerHasProcessedImagery(
  featureData: I.List<I.ImmutableOf<ApiFeatureData>> | null,
  scenes: I.ImmutableListOf<[string, string]> | undefined,
  layerKey: string
): boolean {
  //filter our featureData down to data associated with this layerKey
  const filteredFeatureData = featureUtils.filterFeatureData(
    featureData?.toList() || I.List([]),
    layerKey,
    SORT_DESC
  );

  //check whether the featureData associated to this layerKey has at least one processed image
  return filteredFeatureData.some(
    (featureDatum) =>
      !!featureDatum &&
      featureUtils.getOrderImageryState(scenes, featureDatum!, layerKey) !==
        featureUtils.ORDER_IMAGERY_STATE_AVAILABLE
  );
}

export function hasOnlyUnorderedImagery(
  featureData: I.List<I.ImmutableOf<ApiFeatureData>> | null,
  scenes: I.ImmutableListOf<[string, string]> | undefined,
  layerKey: string
) {
  // we always want to show the Basemap
  if (layerKey === NONE) {
    return false;
  }

  //unordered high res data layers won't actually have any imagery processed associated with a
  //high res data layerKey so we can return false here
  if (layerUtils.isLayerKeyGroupedHighResData(layerKey)) {
    return false;
  }
  //filter our featureData down to data associated with this layerKey
  const filteredFeatureData = featureData
    ? featureUtils.filterFeatureData(featureData.toList(), layerKey, SORT_DESC)
    : I.List<I.ImmutableOf<ApiFeatureData>>();

  //check whether the featureData associated to this layerKey has at least one processed image
  const hasAtLeastOneProcessedImage = filteredFeatureData.some(
    (featureDatum) =>
      !!featureDatum &&
      featureUtils.getOrderImageryState(scenes, featureDatum, layerKey) !==
        featureUtils.ORDER_IMAGERY_STATE_AVAILABLE
  );

  return !!filteredFeatureData.size && !hasAtLeastOneProcessedImage;
}
